// @ts-strict-ignore
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, HostBinding, Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  defer,
  Observable,
  of
} from 'rxjs';
import { SohoBusyIndicatorDirective, SohoToastService } from 'ids-enterprise-ng';
import {
  LMLoggerService,
  LMFormatUtilService,
  LMUrlUtilService,
  LMBusyIndicatorService,
  DrillPointsParams,
  LMContextService
} from '@services/index';
import { AppConstants, LMConditionUtilService, LMUniqueIdService } from '@utils/index';
import {
  LMListCardService,
  LMListEventService,
  LMListGridPagingService, LMListHierarchyService,
  LMListSpecUtilService,
  LMListToolbarService, LMListUtilService
} from './services';
import { LMListContentBaseComponent } from './lm-list-content-base.component';
import * as urlTemplate from 'url-template';
import { LMThemeService } from '@banner/lm-theme.service';

@Component({
  selector: 'lm-list-hierarchy',
  standalone: false,
  templateUrl: './lm-list-hierarchy.component.html',
  styleUrls:   [ 'lm-list-hierarchy.component.scss' ],
  providers: [ LMListHierarchyService ],
  changeDetection: ChangeDetectionStrategy.OnPush
  })
export class LMListHierarchyComponent extends LMListContentBaseComponent implements OnInit, OnDestroy, AfterViewChecked {
  public data: Array<LMStackedHierarchyData<LMHierarchyDataView>>;

  public legend: Array<SohoHierarchyLegend>;
  public leafTemplateId = 'hierarchyChartTemplate';
  public hierarchyChartId: string;
  public legendKey: string = null;
  public leafTemplate: any;
  public recordCount = 0;
  public dataViewSet: LMDataViewSet;
  public initialBuildComplete = false;
  public multiRootUrl: string;
  public initialSectionPerformed = false;
  public uiLoadingIndicatorTimeout = 0;

  // Should not load data on initial load if LPL does not have "use as filter":
  // search form is <FORM_NAME>
  //    use as filter
  public allowFilterResults: boolean;
  private _isMultiRoot = false;

  private set isMultiRoot(isMultiRoot: boolean) {
    this.listState.isMultiRoot = isMultiRoot;
    this._isMultiRoot = isMultiRoot;
  }

  private get isMultiRoot(): boolean {
    return this._isMultiRoot || this?.listState?.isMultiRoot;
  }

  // Checks if multi root after initial load
  public checkIfMultiRoot(data: Array<LMStackedHierarchyData<LMHierarchyDataView>>) {
    let multiRoot = false;
    
    // Is multi root...
    // If initial data has more than one child
    // And ancestorPath && centeredNode are undefined or null
    if (data 
      && data[0] 
      && data[0]?.children?.length > 1 
      && !data[0]?.ancestorPath && !data[0].centeredNode
    ) {
      multiRoot = true;
    }

    // Keep track of multi root
    this.isMultiRoot = multiRoot;
  }

  public get displayMultiRootNavigation(): boolean {
    if (this.data === undefined || this.data === null) {
      return false;
    }

    if (!this.data[0]?.ancestorPath && !this.data[0].centeredNode) {

      // Filtered results do not have ancestorPath or centeredNode
      if (this.filterCriteria && this.filterCriteria.length > 0 && this.isMultiRoot) {
        return true;
      }

      return false;
    }

    return this.isMultiRoot;
  }

  private searchDataView: LMDataView;
  private pageSize = 10;
  private selectedResource: LMHierarchySelectedResource;
  private m_hasFilterList = false;
  public showEmptyMessage = false;

  @ViewChild('SohoHierarchy') sohoHierarchy; // : SohoHierarchyComponent;
  @ViewChild(SohoBusyIndicatorDirective) sohoBusyIndicator: SohoBusyIndicatorDirective;

  @Input() set hasFilterList(hasFilterList: boolean) {
    this.m_hasFilterList = hasFilterList;
  }

  get hasFilterList(): boolean {
    return this.m_hasFilterList;
  }

  @HostBinding('class.lm-parent-is-dashboard') @Input() isDashBoardContent: boolean;

  constructor(
    private ngZOne: NgZone,
    protected lmLoggerService: LMLoggerService,
    protected lmThemeService: LMThemeService,
    protected lmFormatUtilService: LMFormatUtilService,
    protected lmConditionUtilService: LMConditionUtilService,
    protected lmContextService: LMContextService,
    private changeDetectorRef: ChangeDetectorRef,
    private domSanitizer: DomSanitizer,
    private lmListEventService: LMListEventService,
    private lmListGridPagingService: LMListGridPagingService,
    private lmListToolbarService: LMListToolbarService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmListHierarchyService: LMListHierarchyService,
    protected lmListUtilService: LMListUtilService,
    private lmUniqueIdService: LMUniqueIdService
  ) {
    super(lmLoggerService, lmThemeService, lmFormatUtilService, lmListUtilService, lmConditionUtilService);
    this.lmListHierarchyService.lmListEventService = this.lmListEventService;
    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
  }

  ngOnInit() {
    this.clearEmptyMessage();
    this.allowFilterResults = !this.lmListUtilService.isUseAsSearchTargetList(this.listSpec);

    // Builds a template and appends it to DOM for jQuery control
    // Hierarchy control uses the template to render the leafs
    this.buildLeafTemplateForJqueryControl();
    this.validateInitialInputs();
    this.build();
    this.initialBuildComplete = true;
  }

  ngAfterViewChecked() {
    if (this.sohoHierarchy && !this.initialSectionPerformed) {
      this.ngZOne.runOutsideAngular(() => setTimeout(() => this.selectInitialLeaf()));
      this.initialSectionPerformed = true;
    }
  }

  ngOnDestroy() {
    this.setComponentDestroyed();
    this.cancelOutstandingPagingRequest();
  }

  public build(reload?: boolean) {
    this.hierarchyChartId = `hierarchyId_${this.lmListHierarchyService.getPrefix(this.listSpec.resourceId)}_${this.lmUniqueIdService.getUniqueId()}`;
    this.buildGridOptions();

    if (this.listState && this.listState.hierarchyData && this.listState.selectedHierarchyDataView.centeredNode !== null && !this.listSpec.dependentList) {
      this.load(this.listState.selectedHierarchyDataView.centeredNode);
    } else if (this.filterCriteria || this.listState.filterCriteria && this.listState.filterCriteria.length > 0) {
      this.filter();
    } else {
      this.loadInitial(reload);
    }
  }

  protected loadManualData(): void {
    if (this.listSpec.dependentList && !this.componentDestroyed) {
      this.build();
    }
  }

  protected onFormButtonClicked(columnDef: ListGridColumn, dataView: LMDataView, row: any, cell: any, navigationParams: any, event: any): void {
    // Handle click of the no data message button
    if (columnDef.displayField.linkNavigation ||
        columnDef.displayField.navigation ||
        columnDef.displayField.httpUrl ||
        navigationParams
    ) {
      const uiLinkEvent: LMUILinkEvent = { columnDef, dataView, event, navigationParams, options: { fromNoDataMessageFormButton: true } };
      this.lmListEventService.uiLinkEvent(uiLinkEvent);
    } else if (columnDef.displayField.type === AppConstants.FORMBUTTON ||
        columnDef.displayField.type === AppConstants.SUBFORM ||
        columnDef.displayField.type === AppConstants.FORMREFERENCE ||
        columnDef.displayField.type === AppConstants.LISTREFERENCE) {
      this.lmListEventService.formButtonEvent(<LMFormButtonEvent>{ columnDef, dataView, row, cell, event });
    }
  }

  protected fromDataViewChanged(): void {
    // An list form panel on a composite form panel
    // sets the fromDataView to null when the composite form
    // dataView keys change. It does this so the component will
    // not fetch data until its the active panel.

    if (this.listSpec.dependentList && !this.componentDestroyed && this.initialBuildComplete) {
      this.build();
    }
  }

  public updateDataViewTransaction(key: number, status: string, message: string): void {
    // TODO implement me.
  }

  public updateDataView(key: number, dataView: LMDataView) {
    // TODO implement me.
  }

  protected filterCriteriaChanged() {
    if (this.filterCriteria !== undefined && this.initialBuildComplete) {
      this.filter();
    }
  }

  protected filterListCriteriaChanged() {
    // no-op
  }

  protected reportAsOfDateChanged(): void {
      //noop
  }

  public buildGridOptions() {
    return;
  }

  public setSelectedRows() {
    return;
  }

  // overrides the base
  public getDataViewSet(): LMDataViewSet {
    // Safety check, don't mutate if object has been frozen
    if (!Object.isFrozen(this.dataViewSet)) {
      this.dataViewSet.data = this.lmListHierarchyService.getFlatDataViewArray();
    }
    return this.dataViewSet;
  }

  public filter() {
    this.allowFilterResults = true;
    this.lmListHierarchyService.handleCancellableRequest();
    this.cancelOutstandingPagingRequest();
    const pageSize = this.listSpec.pageSize ? this.listSpec.pageSize : this.pageSize;
    const postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
    const compiledUrlTemplate: any = urlTemplate.parse(this.listUrlInfo.initialDataUrl);
    const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined });
    // const filterCriteria = this.listState.filterCriteria ? this.listState.filterCriteria : this.filterCriteria;

    this.pagingSubscription = this.lmListGridPagingService
      .filterPage(url, this.getCombinedFilterCriteria(), pageSize, postContent)
      .subscribe({
        next: (listPagingResponse: LMListPagingResponse) => {

          if (this.lmListHierarchyService.handleCancelableResponse(listPagingResponse)) {
            if (listPagingResponse.status === 'CANCELED') {
              this.lmBusyIndicatorService.hideBusyIndicator();
            }
            return;
          }

          this.setEmptyMessage();
          if (listPagingResponse.status === AppConstants.STATUS_COMPLETED) {

            if (listPagingResponse.dataViewSet === undefined
              || (listPagingResponse.dataViewSet.positionToDataView === undefined
                && listPagingResponse.dataViewSet.data === undefined)
            ) {
              this.handleError('filter() Failed', 'Failed to filter hierarchy data');
            }

            this.dataViewSet = listPagingResponse.dataViewSet;
            this.savedSearches = listPagingResponse.savedSearchDataViewSet;

            const dataViewArray = listPagingResponse.dataViewSet.data;
            const positionToDataView = this.dataViewSet.positionToDataView;
            const dataView = positionToDataView ? positionToDataView[positionToDataView.length - 1] : this.dataViewSet.data[0];

            const hasNext = !!this.dataViewSet?.pagingInfo?.hasNext;
            const nextPageUrl = hasNext ? this.dataViewSet.pagingUrls.nextPageUrl : null;

            this.lmListHierarchyService
              .loadData(this.listSpec, dataViewArray, this.dataViewSet.legendFieldId, dataView, false, true, hasNext, nextPageUrl)
              .then((d) => {
                this.updateView(d, listPagingResponse);

                if (this.sohoHierarchy) {
                  this.sohoHierarchy.reloadDataSet(this.data);
                }

                if (dataViewArray.length === 0) {
                  // need to fire an event so the list will update the detail sections!
                  const listRowData: Array<LMListRowData> = [ {
                    idx: 0,
                    data: {},
                    elem: null
                  } ];
                  this.lmListEventService.listRowSelectedEvent(<LMListRowSelectedEvent>{
                    rows: listRowData,
                    event: {}
                  });
                } else {
                  // after a filter select the first record
                  this.selectLeaf('0');
                }
              });
          } else {
            this.handleError(listPagingResponse.details, listPagingResponse.statusText);
          }
        },
        error: (error) => {
          this.handleError(error.stack, error.message);
        }
      });

  }

  public refresh(): Observable<boolean> {
    if (this.isBusyPaging()) {
      return of (true);
    }

    this.build(true);
    // DPB this needs some refactoring! Need this to be a true subscribe, meaning wait until the data is returned.
    return new Observable<boolean>(observer => {
      observer.next(true);
      observer.complete();
    });
  }

  /**
   *  Events
   */

  public getSelectedRows(): Array<number> {

    if (this.data === undefined || this.listState.selectedHierarchyDataView === undefined) {
      return [ 0 ];
    }

    const selectedRows = [];

    this.lmListHierarchyService.getFlatDataViewArray().forEach((dataView, i) => {
      if (dataView.resourceId === this.listState.selectedHierarchyDataView.resourceId) {
        selectedRows.push(i);
      }
    });

    return selectedRows;
  }

  public getSelectedRow(): number {
    return this.getSelectedRows()[0];
  }

  public unSelectAllRows(): void {
    // TODO: implement
  }

  public activateRow(row: number, scrollIntoView?: boolean): void {
    // TODO: implement
  }

  public activatedRow(): number {
    // TODO: implement
    return 0;
  }

  public addDataView(dataView: LMDataView, index: number) {
    // TODO: implement
  }

  public removeDataView(index: number) {
    // TODO: implement
  }

  public onRowsSelected(event: SohoHierarchyEvent) {
    this.lmLoggerService.log(event);
    const dataView: LMDataView = this.lmListHierarchyService.getFlatDataViewArray()[Number(event.data.id)];

    // todo: ppatton khollenbeck use listSpec.childrenUrl and listSpec.centeredUrl url templates
    // const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined});
    // const compiledUrlTemplate: any = urlTemplate.parse(listSpec.childrenUrl);
    // const childrenUrl = compiledUrlTemplate.expand({ resourceId: dataView.resourceId });

    const childrenUrl = dataView.childrenUrl;

    const centeredNode: LMHierarchyCenteredNodeState = {
      positionToUrl: dataView.positionToUrl,
      childrenUrl,
      fields: dataView.fields,
      resourceId: dataView.resourceId
    };
    this.selectedResource = { resourceId: dataView.resourceId, centeredNode };
    this.saveState();

    if (event.isExpandEvent) {
      this.load(dataView);

      // Used for displaying multi root navigation
    } else if (!childrenUrl || !centeredNode.positionToUrl) {
      if (event && event.targetInfo
        && event.targetInfo.target && event.targetInfo.target.classList.contains('btn-collapse')
      ) {
        event.targetInfo.target.style.display = 'none';
      }
    }

    if (event.isCollapseEvent || event.isBackEvent) {
      this.load(dataView);
    }

    if (event.isActionsEvent) {
      defer(async () => await this.getActions()).subscribe((actions) => {
        this.sohoHierarchy?.updateActions(event, actions);
      });
    }

    if (event.isActionEvent && event.actionReference) {
      let actionRef = event.actionReference.actionReference;
      if (event.actionReference.menu) {
        // item in a submenu clicked, find the correct action reference
        event.actionReference.menu.forEach((menu) => {
          if (menu.actionReference.label === event.targetInfo.target.innerText) {
            actionRef = menu.actionReference;
          }
        });
      }
      this.lmListEventService.hierarchyActionMenuItemSelectedEvent(actionRef);
    }

    if (event.isSelectedEvent) {
      if (event?.targetInfo?.target?.classList.contains('lm-more-link')
          && event?.targetInfo?.target?.parentElement?.classList.contains('detail')) {
        this.loadMoreRecords(dataView.nextPageUrl, dataView.parentDataView);
      } else {
        const listRowData: Array<LMListRowData> = [ {
          idx: this.getSelectedRows()[0],
          data: dataView,
          elem: event.targetInfo ? event.targetInfo.target : null
        } ];
        this.lmListEventService.listRowSelectedEvent(<LMListRowSelectedEvent>{
          rows: listRowData,
          event: event.targetInfo
        });
      }
    }
  }

  public getDirtyRows(): Array<LMDataView> {
    return;
  }
  public isModified(): boolean {
    return;
  }
  public disableSaveInUserListViewMenu(): boolean {
    // TODO: The saved search  depends on filter criteria.
    // TODO: Enable or disable based on sort order that is part of LMSearchState.
    if (!this.listState.filterCriteria?.length)
      return true;
    else if (this.listState.filterCriteria.some(fc => fc.isAdHoc === true))
      return true;
    else
      return false; //Save is enabled when at least 1 filter criteria exists and none of them are ad hoc 
  }
  public onDoubleClick(event: SohoHierarchyDoubleClickEvent) {
    const dataView: LMDataView = this.lmListHierarchyService.getFlatDataViewArray()[Number(event.data.id)];

    // todo: ppatton khollenbeck use listSpec.childrenUrl and listSpec.centeredUrl url templates
    // const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined});
    // const compiledUrlTemplate: any = urlTemplate.parse(listSpec.childrenUrl);
    // const childrenUrl = compiledUrlTemplate.expand({ resourceId: dataView.resourceId });

    const childrenUrl = dataView.childrenUrl;
    const centeredNode: LMHierarchyCenteredNodeState = {
      positionToUrl: dataView.positionToUrl,
      childrenUrl,
      fields: dataView.fields,
      resourceId: dataView.resourceId
    };

    this.selectedResource = { resourceId: dataView.resourceId, centeredNode };
    this.lmListEventService.listHierarchyDoubleClickedEvent(event.data);
  }

  /**
   *  END PUBLIC Events
   */
  private loadInitial(reload?: boolean) {
    this.lmListHierarchyService.handleCancellableRequest();
    this.cancelOutstandingPagingRequest();
    let initialUrl = this.listUrlInfo.initialDataUrl;

    if (this.multiRootUrl && !this.listSpec.dependentList) {
      initialUrl = this.multiRootUrl;
    }

    const pageSize = this.listSpec.pageSize ? this.listSpec.pageSize : this.pageSize;
    const postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
    const isManualLoad = this.isManualLoading();
    const compiledUrlTemplate: any = urlTemplate.parse(initialUrl);
    const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined });

    this.pagingSubscription = this.lmListGridPagingService
      .initialPageFiltered(url, this.getCombinedFilterCriteria(), pageSize, postContent, isManualLoad)
      .subscribe({
        next: (listPagingResponse: LMListPagingResponse) => {

          if (this.lmListHierarchyService.handleCancelableResponse(listPagingResponse)) {
            if (listPagingResponse.status === 'CANCELED') {
              this.lmBusyIndicatorService.hideBusyIndicator();
            }
            return;
          }

          this.setEmptyMessage();
          if (listPagingResponse.status === AppConstants.STATUS_COMPLETED) {
            if (listPagingResponse.dataViewSet === undefined
              || (listPagingResponse.dataViewSet.positionToDataView === undefined && listPagingResponse.dataViewSet.data === undefined)
            ) {
              this.handleError('LoadInitial() Failed', 'Failed to load initial data');
            }

            this.searchDataView = listPagingResponse.searchDataView;
            this.dataViewSet = listPagingResponse.dataViewSet;
            this.savedSearches = listPagingResponse.savedSearchDataViewSet;

            const hasNext = !!this.dataViewSet?.pagingInfo?.hasNext;
            const nextPageUrl = hasNext ? this.dataViewSet.pagingUrls.nextPageUrl : null;

            if (!this.dataViewSet.positionToDataView && this.dataViewSet.data.length > 1) {
              this.multiRootUrl = url;
              this.lmListHierarchyService.loadData(this.listSpec, this.dataViewSet.data, this.dataViewSet.legendFieldId, null, false, false, hasNext, nextPageUrl).then((data) => {
                
                this.checkIfMultiRoot(data);
                this.updateView(data, listPagingResponse);

                if (reload) {
                  this.sohoHierarchy?.reloadDataSet(data);
                }
              });
            } else {
              const dataView = this.dataViewSet.positionToDataView ? this.dataViewSet.positionToDataView[this.dataViewSet.positionToDataView.length - 1] : this.dataViewSet.data[0];
              this.lmListHierarchyService.loadData(this.listSpec, this.dataViewSet.data, this.dataViewSet.legendFieldId, dataView, true).then((data) => {
                
                this.checkIfMultiRoot(data);
                this.updateView(data, listPagingResponse);
                this.sohoHierarchy?.reloadDataSet(data);
              });
            }
          } else {
            this.handleError(listPagingResponse.details, listPagingResponse.statusText);
          }
        },
        error: (error) => {
          this.handleError(error.stack, error.message);
        }
      });
  }

  public loadMultiRoot() {
    this.lmListEventService.clearSearchCriteriaEvent();
    if (this.multiRootUrl || this.listState.multiRootUrl) {
      this.loadInitial(true);
    }
  }

  private loadMoreRecords(nextPageUrl: string, parentDataView: LMListDataView) {
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    this.lmListHierarchyService.handleCancellableRequest();
    this.cancelOutstandingPagingRequest();

    let url = nextPageUrl;

    if (parentDataView) {
      const delimiter = (url.indexOf('?') === -1) ? '?' : '&';
      url = url + delimiter + 'parent=' + parentDataView.resourceId;
    }

    const pageSize = this.listSpec.pageSize ? this.listSpec.pageSize : this.pageSize;
    const postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);

    this.pagingSubscription = this.lmListGridPagingService
      .initialPageFiltered(url, this.getCombinedFilterCriteria(), pageSize, postContent)
      .subscribe({
        next: (listPagingResponse: LMListPagingResponse) => {

        if (this.lmListHierarchyService.handleCancelableResponse(listPagingResponse)) {
          if (listPagingResponse.status === 'CANCELED') {
            this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
          }
          return;
        }

        if (listPagingResponse.status === AppConstants.STATUS_COMPLETED) {
          if (listPagingResponse.dataViewSet === undefined
              || (listPagingResponse.dataViewSet.positionToDataView === undefined && listPagingResponse.dataViewSet.data === undefined)
          ) {
            this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
            this.handleError('LoadMoreRecords() Failed', 'Failed to load more data');
          }

          this.searchDataView = listPagingResponse.searchDataView;
          this.dataViewSet = listPagingResponse.dataViewSet;
          this.savedSearches = listPagingResponse.savedSearchDataViewSet;

          const hasNext = !!this.dataViewSet?.pagingInfo?.hasNext;
          nextPageUrl = hasNext ? this.dataViewSet.pagingUrls.nextPageUrl : null;

          let dataViewArray = this.lmListHierarchyService.getFlatDataViewArray();
          dataViewArray.pop();
          dataViewArray = dataViewArray.concat(this.dataViewSet.data);

          if (parentDataView) {
            dataViewArray.splice(0, dataViewArray.findIndex(item => item.resourceId === parentDataView.resourceId) + 1);
          }

          if (!this.dataViewSet.positionToDataView && dataViewArray.length > 1) {
            const centeredNode = parentDataView ? parentDataView : null;
            this.lmListHierarchyService.loadData(this.listSpec, dataViewArray, this.dataViewSet.legendFieldId, centeredNode, false, false, hasNext, nextPageUrl).then((data) => {
              this.updateView(data, listPagingResponse);

              if (this.sohoHierarchy) {
                this.sohoHierarchy.reloadDataSet(data);
              }
            });
          } else {
            const dataView = this.dataViewSet.positionToDataView ? this.dataViewSet.positionToDataView[this.dataViewSet.positionToDataView.length - 1] : this.dataViewSet.data[0];
            this.lmListHierarchyService.loadData(this.listSpec, dataViewArray, this.dataViewSet.legendFieldId, dataView, true, false, hasNext, nextPageUrl).then((data) => {
              this.updateView(data, listPagingResponse);
            });
          }

          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        }
      },
      error: (error) => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.handleError(error.stack, error.message);
      }
      });
  }

  private load(data: LMDataView|LMHierarchyCenteredNodeState) {
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    this.lmListHierarchyService.handleCancellableRequest();
    this.cancelOutstandingPagingRequest();
    this.pageSize = this.listSpec.pageSize ? this.listSpec.pageSize : this.pageSize;

    // todo: ppatton khollenbeck use listSpec.childrenUrl and listSpec.centeredUrl url templates
    // const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined});
    // const compiledUrlTemplate: any = urlTemplate.parse(listSpec.childrenUrl);
    // const childrenUrl = compiledUrlTemplate.expand({ resourceId: dataView.resourceId });

    const childrenUrl = data.childrenUrl || data.positionToUrl;

    this.pagingSubscription = this.lmListGridPagingService
      .initialPageFiltered(childrenUrl, this.getCombinedFilterCriteria(), this.pageSize, undefined)
      .subscribe({
        next: (listPagingResponse: LMListPagingResponse) => {

          if (this.lmListHierarchyService.handleCancelableResponse(listPagingResponse)) {
            if (listPagingResponse.status === 'CANCELED') {
              this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
            }
            return;
          }

          // Loaded from state need to set dataViewSet
          if (this.listState && this.dataViewSet === undefined) {
            this.searchDataView = listPagingResponse.searchDataView;
          }

          this.dataViewSet = listPagingResponse.dataViewSet;
          this.savedSearches = listPagingResponse.savedSearchDataViewSet;

          const hasNext = !!this.dataViewSet?.pagingInfo?.hasNext;
          const nextPageUrl = hasNext ? this.dataViewSet.pagingUrls.nextPageUrl : null;

          const dataView = data as LMDataView;
          this.lmListHierarchyService.loadData(this.listSpec, listPagingResponse.dataViewSet.data, this.dataViewSet.legendFieldId, dataView, true, false, hasNext, nextPageUrl).then((d) => {
            this.updateView(d, listPagingResponse);

            if (this.sohoHierarchy) {
              this.sohoHierarchy.reloadDataSet(this.data);
              this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
            }

            this.initialSectionPerformed = false;
          });
        },
        error: (error) => {
          this.handleError(error.stack, error.message);
        }
      });
  }

  private updateView(data: Array<SohoStackedHierarchyData<LMHierarchyDataView>>, listPagingResponse: LMListPagingResponse) {
    this.data = data;
    this.recordCount = this.data && this.allowFilterResults ? this.data.length : 0;
    this.showEmptyMessage = (this.recordCount === 0) ? true : false;
    this.legendKey = (this.dataViewSet && this.dataViewSet.legendFieldId) ? this.dataViewSet.legendFieldId : this.listState.legendFieldId;
    this.legend = (this.dataViewSet && this.dataViewSet.legendMap) ? this.dataViewSet.legendMap : this.listState.legendMap;

    if (this.searchDataView) {
      listPagingResponse.searchDataView = this.searchDataView;
    } else if (this.listState.searchFormDataView) {
      listPagingResponse.searchDataView = this.listState.searchFormDataView;
    }

    this.lmBusyIndicatorService.hideBusyIndicator();
    this.changeDetectorRef.markForCheck();
    this.lmListEventService.dataLoadedEvent(listPagingResponse);
  }

  private selectInitialLeaf() {
    this.selectLeaf(this.getInitialLeafId());
  }

  private selectLeaf(leafId: string) {
    if (this.sohoHierarchy) {

      this.sohoHierarchy.selectLeaf(leafId);

      if (document.getElementById(leafId)) {
        document.getElementById(leafId).scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }
  }

  private getActions(): Promise<Array<SohoHierarchyAction>> {
    return new Promise<Array<SohoHierarchyAction>>((resolve, reject) => {
      // Need to override the data on listState DataViewSet to get the appropriate actions
      this.listState.dataViewSet.data = this.lmListHierarchyService.getFlatDataViewArray();
      this.listState.selectedRows = this.getSelectedRows();
      let actions = [];

      const contextMenuItems = this.lmListToolbarService.getContextMenu(this.listSpec, this.listState, this.listUrlInfo);

      const drillPointsParams: DrillPointsParams = {
        dataView: this.listState.dataViewSet.data[ 0 ],
        fieldId:  this.listSpec.busListSpec.id
      };
      defer(async () => await this.lmListToolbarService.lmToolbarServiceAdapter.lmDrillService.getDrillPoints(drillPointsParams).toPromise()).subscribe((drillResponse: LMDrillResponse) => {
        const drillPoints = drillResponse.drillPoints;
        actions = contextMenuItems.map((item: LMToolbarButton) => {
          const hasNoLabel = item.actionReference.label === undefined;
          const hasToolTip = item.actionReference.tooltip !== undefined;
          const value = hasNoLabel && hasToolTip ? item.actionReference.tooltip : item.actionReference.label;
          if (item.menu) {
            if (item.actionReference.id === AppConstants.STANDARD_TOOLBAR_RELATEDMENU_BUTTON) {
              if (drillPoints.length > 0) {
                const actionRefs: Array<LMBusActionReference> = this.lmListToolbarService.drillActionsToActionRefArray(drillPoints);

                const subMenuButtons: Array<LMToolbarButton> = this.lmListToolbarService.createToolbarMenuItemsFromActionReferences(this.listSpec, actionRefs);
                item.menu = subMenuButtons;
              } else {
                const noActionMenu: LMToolbarButton = {
                  actionReference: { id: 'noop', type: 'noop', label: Soho.Locale.translate('NoDrillsAvailable') },
                  state:           { disabled: false, visible: true }
                };
                item.menu = [ noActionMenu ];
              }
            }
            const subMenuItems = item.menu.map((subItem) => {
              const _hasNoLabel = subItem.actionReference.label === undefined;
              const _hasToolTip = subItem.actionReference.tooltip !== undefined;
              const _value = _hasNoLabel && _hasToolTip ? subItem.actionReference.tooltip : subItem.actionReference.label;

              return { value: _value, url: '', data: subItem };
            });

            return { value, url: '', data: item, menu: subMenuItems };
          } else {
            // separators need a specific data, or they will appear as a blank line
            if (item.actionReference.type === 'separator') {
              return { value: '', data: { id: 'separator', type: 'separator', label: '' } };
            }

            return { value, url: '', data: item };
          }
        });

        // Set listState dataViewSet back to original dataViewSet
        this.listState.dataViewSet = this.dataViewSet;

        resolve(actions);
      });
    });
  }

  private getInitialLeafId(): string {
    let id;
    let selectedResource;

    if (this.selectedResource) {
      selectedResource = this.selectedResource.resourceId;
    }
    if (this.selectedResource === undefined
    && (this.listState && this.listState.selectedHierarchyDataView && this.listState.selectedHierarchyDataView.resourceId)
    ) {
      selectedResource = this.listState.selectedHierarchyDataView.resourceId;
    }

    this.lmListHierarchyService.getFlatDataViewArray().forEach((dataView: LMDataView, index) => {
      if (dataView.resourceId === selectedResource) {
        id = index;
      }
    });

    if (this.dataViewSet && this.dataViewSet.positionToDataView) {
      id = this.dataViewSet.positionToDataView.length - 1;
    }

    return id ? id.toString() : '0';
  }

  private buildLeafTemplateForJqueryControl() {
    const leafTemplate = `
      <script type="text/html" id="${this.leafTemplateId}">
        <div class="leaf {{colorClass}}" id="{{id}}">
          {{#randomBackground}}
            {{#missingImage}}
            <span class="image-initials {{bgClass}}">{{foregroundText}}</span>
            {{/missingImage}}
            {{#imageUrl}}
              <img src="{{imageUrl}}" class="image" alt="{{imageTitle}}"/>
            {{/imageUrl}}
          {{/randomBackground}}
          {{^randomBackground}}
              {{#imageUrl}}
              <img src="{{imageUrl}}" class="image" alt="{{imageTitle}}"/>
              {{/imageUrl}}
          {{/randomBackground}}
          {{#doesNotExist}}
            <del>
                <div class="detail">
                  <p class="heading lm-font-lg">{{title}}</p>
                  <p class="subheading">{{subheading}}</p>
                  <p class="micro">{{micro}}</p>
                </div>
            </del>
          {{/doesNotExist}}
          {{^doesNotExist}}
            {{#isHierarchyMoreLink}}
              <div class="detail center">
                <p class="heading">{{title}}</p>
                <a href="javascript:void(0);" class="hyperlink lm-more-link">{{subheading}}</a>
              </div>
            {{/isHierarchyMoreLink}}
            {{^isHierarchyMoreLink}}
              <div class="detail">
                <p class="heading lm-font-lg">{{title}}</p>
                <p class="subheading">{{subheading}}</p>
                <p class="micro">{{micro}}</p>
              </div>
            {{/isHierarchyMoreLink}}
          {{/doesNotExist}}
          {{#menu}}
            <button class="btn-actions btn-icon" type="button" data-init="false" data-options="{'attachToBody': false}" id="btn-{{id}}">
              <svg role="presentation" aria-hidden="true" focusable="false" class="icon">
                <use xlink:href="#icon-more"></use>
              </svg>
              <span class="audible">More Info & Additional Actions</span>
            </button>
            <ul class="popupmenu leaf-content-menu"></ul>
          {{/menu}}
          <!-- show button unless hasNoChildren is true -->
          {{^hasNoChildren}}
          <button class="btn btn-icon" type="button">
            <svg role="presentation" aria-hidden="true" focusable="false" class="icon">
              <use xlink:href="#icon-caret-up"/>
            </svg>
            <span class="audible">Expand/Collapse</span>
          </button>
          {{/hasNoChildren}}
        </div>    
      </script>
    `;

    this.leafTemplate = this.domSanitizer.bypassSecurityTrustHtml(leafTemplate);
  }

  /**
   * Saves hierarchy state
   */
  private saveState() {
    if (this.listSpec.dependentList && this.implementsManualLoad) {
      return;
    }

    const centeredDataViewIndex = this.lmListHierarchyService.getCenteredNodeIndex();
    const centeredDataView = this.lmListHierarchyService.getFlatDataViewArray()[centeredDataViewIndex];
    let centeredNode: LMHierarchyCenteredNodeState;

    // todo: ppatton khollenbeck use listSpec.childrenUrl and listSpec.centeredUrl url templates
    // const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined});
    // const compiledUrlTemplate: any = urlTemplate.parse(listSpec.childrenUrl);
    // const childrenUrl = compiledUrlTemplate.expand({ resourceId: dataView.resourceId });

    if (centeredDataView) {
      const childrenUrl = centeredDataView.childrenUrl;
      centeredNode = {
        positionToUrl: centeredDataView.positionToUrl,
        childrenUrl,
        fields: centeredDataView.fields,
        resourceId: centeredDataView.resourceId
      };

      this.listState.hierarchyData = this.mapHierarchyDataForListState(centeredDataView, centeredDataViewIndex);
    }

    this.listState.selectedHierarchyDataView = this.selectedResource === undefined ?
      { resourceId: centeredDataView.resourceId, centeredNode: centeredDataView ? centeredNode : null } :
      { resourceId: this.selectedResource.resourceId, centeredNode: centeredDataView ? centeredNode : null };

    if (this.dataViewSet.legendFieldId && this.dataViewSet.legendMap) {
      this.listState.legendMap = this.dataViewSet.legendMap;
      this.listState.legendFieldId = this.dataViewSet.legendFieldId;
    }

    if (this.filterCriteria) {
      this.listState.filterCriteria = this.filterCriteria;
    }

    if (this.searchDataView) {
      this.listState.searchFormDataView = this.searchDataView;
    }

    if (this.multiRootUrl) {
      this.listState.multiRootUrl = this.multiRootUrl;
    }

    this.listState.dataViewSet = this.dataViewSet;
    this.listState.selectedRows = this.getSelectedRows();
  }

  private mapHierarchyDataForListState(centeredDataView: LMDataView, centeredDataViewIndex: number): LMHierarchyDataViewState {
    const ancestorResourceIds = [];
    const childrenResourceIds = [];

    this.lmListHierarchyService.getFlatDataViewArray().forEach((dataView, index) => {
      const resourceId = dataView.resourceId;
      if (index < centeredDataViewIndex) {
        ancestorResourceIds.push(resourceId);
      }
      if (index === centeredDataViewIndex) {
        return;
      }
      if (index > centeredDataViewIndex) {
        childrenResourceIds.push(resourceId);
      }
    });

    return {
      ancestors: ancestorResourceIds,
      resourceId: centeredDataView.resourceId,
      children: childrenResourceIds
    };
  }
}
